// 🔧 settings_scoring.js
// ✅ Purpose: Admin-only Scoring Settings page
// ✅ Features:
// - Standalone replacement for old Scoring tab
// - Allows admins/managers to create scoring factors
// - Each scoring factor has a max point value
// - Active scoring factor max points must total the event-configured ballot maximum
// - Allows admins/managers to add key highlights under each scoring factor
// - Supports edit/delete for scoring factors
// - Supports add/delete for key highlights
// - Uses event-scoped Firestore collections
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import {
  calculateAvailablePoints,
  validateScoringFactorsTotal,
} from "../../utils/points_helpers";

const SettingsScoring = () => {
  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ✅ Organizer-controlled judged ballot maximum from settings/voting.
  // ✅ null means fall back to the Legacy root event value, then 100.
  const [
    votingSettingsMaxBallotPoints,
    setVotingSettingsMaxBallotPoints,
  ] = useState(null);

  const [scoringFactors, setScoringFactors] = useState([]);
  const [highlightsByFactor, setHighlightsByFactor] = useState({});

  const [newFactorName, setNewFactorName] = useState("");
  const [newFactorMaxPoints, setNewFactorMaxPoints] = useState("");
  const [newFactorSortOrder, setNewFactorSortOrder] = useState("");

  const [editingFactorId, setEditingFactorId] = useState(null);
  const [editingFactorName, setEditingFactorName] = useState("");
  const [editingFactorMaxPoints, setEditingFactorMaxPoints] = useState("");
  const [editingFactorSortOrder, setEditingFactorSortOrder] = useState("");

  const [newHighlightByFactor, setNewHighlightByFactor] = useState({});
  const [
    newHighlightMaxPointsByFactor,
    setNewHighlightMaxPointsByFactor,
  ] = useState({});

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  const showScoringPage =
    eventDoc?.votingType === "points" ||
    eventDoc?.votingType === "scorecard";

  // ✅ Resolve the maximum total score for this event.
  // ✅ Existing events without maxBallotPoints remain 100-point events.
  // ✅ Events may explicitly use another total such as 25, 50, or 100.
  const maxBallotPoints =
    Number(
      votingSettingsMaxBallotPoints ??
      eventDoc?.maxBallotPoints ??
      100
    );

  const totalAvailablePoints =
    calculateAvailablePoints(scoringFactors);

  // ✅ Validate scoring factors against the configured event maximum
  // instead of assuming every judged event uses 100 points.
  const factorValidation =
    validateScoringFactorsTotal(
      scoringFactors,
      maxBallotPoints
    );

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!eventId || !showScoringPage) return;

    loadScoringSetup();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, showScoringPage]);

  const loadScoringSetup = async () => {
    setLoading(true);

    try {
      console.log("Loading scoring setup for event:", eventId);

      // ✅ Load the organizer-controlled judged ballot maximum.
      // ✅ settings/voting is authoritative for new configuration.
      // ✅ Root event maxBallotPoints remains a Legacy compatibility fallback.
      const votingSettingsSnap = await getDoc(
        doc(db, "events", eventId, "settings", "voting")
      );

      const votingSettingsData =
        votingSettingsSnap.exists()
          ? votingSettingsSnap.data()
          : {};

      setVotingSettingsMaxBallotPoints(
        votingSettingsData.maxBallotPoints ?? null
      );

      console.log("Resolved scoring ballot maximum source:", {
        settingsMaxBallotPoints:
          votingSettingsData.maxBallotPoints ?? null,
        legacyEventMaxBallotPoints:
          eventDoc?.maxBallotPoints ?? null,
        resolvedMaxBallotPoints: Number(
          votingSettingsData.maxBallotPoints ??
          eventDoc?.maxBallotPoints ??
          100
        ),
      });

      const factorsRef = collection(
        db,
        "events",
        eventId,
        "scoringFactors"
      );

      const factorsQuery = query(
        factorsRef,
        orderBy("sortOrder", "asc")
      );

      const factorsSnap = await getDocs(factorsQuery);

      const loadedFactors = factorsSnap.docs.map((factorDoc) => ({
        id: factorDoc.id,
        ...factorDoc.data(),
      }));

      const loadedHighlights = {};

      for (const factor of loadedFactors) {
        const highlightsRef = collection(
          db,
          "events",
          eventId,
          "scoringFactors",
          factor.id,
          "highlights"
        );

        const highlightsQuery = query(
          highlightsRef,
          orderBy("sortOrder", "asc")
        );

        const highlightsSnap = await getDocs(highlightsQuery);

        loadedHighlights[factor.id] = highlightsSnap.docs.map(
          (highlightDoc) => ({
            id: highlightDoc.id,
            ...highlightDoc.data(),
          })
        );
      }

      setScoringFactors(loadedFactors);
      setHighlightsByFactor(loadedHighlights);
    } catch (error) {
      console.error("Error loading scoring setup:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to load scoring setup.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddFactor = async (e) => {
    e.preventDefault();

    const cleanName = newFactorName.trim();
    const maxPoints = Number(newFactorMaxPoints);
    const sortOrder = Number(
      newFactorSortOrder || scoringFactors.length + 1
    );

    if (!cleanName) {
      showStatus(setStatus, {
        type: "warning",
        message: "Scoring factor name is required.",
      });

      return;
    }

    if (!maxPoints || maxPoints <= 0) {
      showStatus(setStatus, {
        type: "warning",
        message: "Max points must be greater than 0.",
      });

      return;
    }

    setSaving(true);

    try {
      await addDoc(
        collection(db, "events", eventId, "scoringFactors"),
        {
          name: cleanName,
          maxPoints,
          sortOrder,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      setNewFactorName("");
      setNewFactorMaxPoints("");
      setNewFactorSortOrder("");

      showStatus(setStatus, {
        type: "success",
        message: "Scoring factor added.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error adding scoring factor:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to add scoring factor.",
      });
    } finally {
      setSaving(false);
    }
  };

  const startEditFactor = (factor) => {
    setEditingFactorId(factor.id);
    setEditingFactorName(factor.name || "");
    setEditingFactorMaxPoints(String(factor.maxPoints || ""));
    setEditingFactorSortOrder(String(factor.sortOrder || ""));
  };

  const cancelEditFactor = () => {
    setEditingFactorId(null);
    setEditingFactorName("");
    setEditingFactorMaxPoints("");
    setEditingFactorSortOrder("");
  };

  const handleSaveFactor = async (factorId) => {
    const cleanName = editingFactorName.trim();
    const maxPoints = Number(editingFactorMaxPoints);
    const sortOrder = Number(editingFactorSortOrder || 0);

    if (!cleanName) {
      showStatus(setStatus, {
        type: "warning",
        message: "Scoring factor name is required.",
      });

      return;
    }

    if (!maxPoints || maxPoints <= 0) {
      showStatus(setStatus, {
        type: "warning",
        message: "Max points must be greater than 0.",
      });

      return;
    }

    setSaving(true);

    try {
      await updateDoc(
        doc(db, "events", eventId, "scoringFactors", factorId),
        {
          name: cleanName,
          maxPoints,
          sortOrder,
          updatedAt: serverTimestamp(),
        }
      );

      cancelEditFactor();

      showStatus(setStatus, {
        type: "success",
        message: "Scoring factor updated.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error updating scoring factor:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to update scoring factor.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFactorActive = async (factor) => {
    setSaving(true);

    try {
      await updateDoc(
        doc(db, "events", eventId, "scoringFactors", factor.id),
        {
          active: factor.active === false,
          updatedAt: serverTimestamp(),
        }
      );

      showStatus(setStatus, {
        type: "success",
        message:
          factor.active === false
            ? "Scoring factor activated."
            : "Scoring factor deactivated.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error toggling scoring factor:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to update scoring factor status.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFactor = async (factorId) => {
    const confirmed = window.confirm(
      "Delete this scoring factor? This will also delete its key highlights. This should only be done before voting starts."
    );

    if (!confirmed) return;

    setSaving(true);

    try {
      const highlights = highlightsByFactor[factorId] || [];

      for (const highlight of highlights) {
        await deleteDoc(
          doc(
            db,
            "events",
            eventId,
            "scoringFactors",
            factorId,
            "highlights",
            highlight.id
          )
        );
      }

      await deleteDoc(
        doc(db, "events", eventId, "scoringFactors", factorId)
      );

      showStatus(setStatus, {
        type: "success",
        message: "Scoring factor deleted.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error deleting scoring factor:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to delete scoring factor.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddHighlight = async (factorId) => {
    const cleanName =
      (newHighlightByFactor[factorId] || "").trim();

    const maxPoints = Number(
      newHighlightMaxPointsByFactor[factorId] || 0
    );

    if (!cleanName) {
      showStatus(setStatus, {
        type: "warning",
        message: "Key highlight name is required.",
      });

      return;
    }

    const existingHighlights =
      highlightsByFactor[factorId] || [];

    setSaving(true);

    try {
      await addDoc(
        collection(
          db,
          "events",
          eventId,
          "scoringFactors",
          factorId,
          "highlights"
        ),
        {
          name: cleanName,
          maxPoints,
          sortOrder: existingHighlights.length + 1,
          active: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      setNewHighlightByFactor((current) => ({
        ...current,
        [factorId]: "",
      }));

      setNewHighlightMaxPointsByFactor((current) => ({
        ...current,
        [factorId]: "",
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Key highlight added.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error adding key highlight:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to add key highlight.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHighlight = async (factorId, highlightId) => {
    const confirmed = window.confirm("Delete this key highlight?");

    if (!confirmed) return;

    setSaving(true);

    try {
      await deleteDoc(
        doc(
          db,
          "events",
          eventId,
          "scoringFactors",
          factorId,
          "highlights",
          highlightId
        )
      );

      showStatus(setStatus, {
        type: "success",
        message: "Key highlight deleted.",
      });

      await loadScoringSetup();
    } catch (error) {
      console.error("Error deleting key highlight:", error);

      showStatus(setStatus, {
        type: "error",
        message: "Unable to delete key highlight.",
      });
    } finally {
      setSaving(false);
    }
  };

  const activeFactorCount = scoringFactors.filter(
    (factor) => factor.active !== false
  ).length;

  const inactiveFactorCount = scoringFactors.filter(
    (factor) => factor.active === false
  ).length;

  if (!ready || eventLoading || isManager === null) {
    return <FullPageSpinner message="Loading scoring settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  if (!showScoringPage) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="white-section p-4 rounded shadow-sm bg-white">
          <div className="alert alert-warning text-start mb-0">
            Scoring settings are only available for Points or Scorecard voting events.
          </div>
        </div>
      </section>
    );
  }

  if (loading) {
    return <FullPageSpinner message="Loading scoring setup..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🧮 Scoring Settings</h1>
            <p className="lead text-muted mb-0">
              Configure scoring factors, point values, and scorecard highlights.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              type="submit"
              form="add-scoring-factor-form"
              className="btn btn-primary"
              disabled={saving}
            >
              ➕ Add Factor
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 ">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Scoring Factors</div>
                <div className="fs-3 fw-bold">{scoringFactors.length}</div>
                <div className="small text-muted">
                  {activeFactorCount} active / {inactiveFactorCount} inactive
                </div>
              </div>
            </div>
          </div>



          <div className="col-12 col-md-6 ">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Active Points</div>
                <div className="fs-3 fw-bold">{totalAvailablePoints}/{maxBallotPoints}</div>
              </div>
            </div>
          </div>

        </div>

        <div
          className={`alert ${
            factorValidation.isValid ? "alert-success" : "alert-warning"
          }`}
        >
          <strong>
            {factorValidation.isValid
              ? "🟢 Scoring setup is valid."
              : "🟡 Scoring setup needs attention."}
          </strong>

          <div className="small mt-1">
            {factorValidation.message}
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Scoring Tools</strong>
          </div>

          <div className="card-body">
            <form id="add-scoring-factor-form" onSubmit={handleAddFactor}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-lg-5">
                  <label className="form-label">Factor Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newFactorName}
                    onChange={(e) => setNewFactorName(e.target.value)}
                    placeholder="Example: Body, Paint, Interior"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <label className="form-label">Max Points</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newFactorMaxPoints}
                    onChange={(e) => setNewFactorMaxPoints(e.target.value)}
                    placeholder="Example: 25"
                    min="1"
                    max="100"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-2">
                  <label className="form-label">Sort Order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newFactorSortOrder}
                    onChange={(e) => setNewFactorSortOrder(e.target.value)}
                    placeholder="1"
                    min="1"
                  />
                </div>

                <div className="col-12 col-lg-2">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={saving}
                  >
                    ➕ Add
                  </button>
                </div>
              </div>

              <div className="small text-muted mt-2">
                Active scoring factor points should total exactly {maxBallotPoints}.
              </div>
            </form>
          </div>
        </div>

        {scoringFactors.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No scoring factors yet</h4>
              <p className="text-muted mb-4">
                Create your first scoring factor to begin building the judging scorecard.
              </p>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {scoringFactors.map((factor) => {
              const highlights = highlightsByFactor[factor.id] || [];
              const isEditing = editingFactorId === factor.id;
              const isInactive = factor.active === false;

              return (
                <div key={factor.id} className="col-12 col-xl-6">
                  <div
                    className={`card h-100 shadow-sm ${
                      isInactive ? "border-secondary" : "border"
                    }`}
                  >
                    <div className="card-header bg-white">
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                        <div>
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <h4 className="h5 fw-bold mb-0">
                              🧮 {factor.name || "Unnamed Factor"}
                            </h4>

                            <span
                              className={`badge ${
                                isInactive ? "bg-secondary" : "bg-success"
                              }`}
                            >
                              {isInactive ? "Inactive" : "Active"}
                            </span>
                          </div>

                          <div className="small text-muted">
                            {factor.maxPoints || 0} points · Sort order{" "}
                            {factor.sortOrder || "—"} · {highlights.length} highlight
                            {highlights.length !== 1 ? "s" : ""}
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 align-self-start">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleToggleFactorActive(factor)}
                            disabled={saving}
                          >
                            {isInactive ? "Activate" : "Deactivate"}
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => startEditFactor(factor)}
                            disabled={saving}
                          >
                            ✏️ Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteFactor(factor.id)}
                            disabled={saving}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card-body">
                      {isEditing && (
                        <div className="border rounded p-3 mb-4 bg-light">
                          <h5 className="h6 fw-bold mb-3">Edit Scoring Factor</h5>

                          <div className="row g-3 align-items-end">
                            <div className="col-12 col-lg-5">
                              <label className="form-label">Factor Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingFactorName}
                                onChange={(e) =>
                                  setEditingFactorName(e.target.value)
                                }
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-3">
                              <label className="form-label">Max Points</label>
                              <input
                                type="number"
                                className="form-control"
                                value={editingFactorMaxPoints}
                                onChange={(e) =>
                                  setEditingFactorMaxPoints(e.target.value)
                                }
                                min="1"
                                max="100"
                              />
                            </div>

                            <div className="col-12 col-md-6 col-lg-2">
                              <label className="form-label">Sort</label>
                              <input
                                type="number"
                                className="form-control"
                                value={editingFactorSortOrder}
                                onChange={(e) =>
                                  setEditingFactorSortOrder(e.target.value)
                                }
                                min="1"
                              />
                            </div>

                            <div className="col-12 col-lg-2 d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-success w-100"
                                onClick={() => handleSaveFactor(factor.id)}
                                disabled={saving}
                              >
                                Save
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={cancelEditFactor}
                                disabled={saving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5 className="h6 fw-bold mb-0">Key Highlights</h5>

                          <span className="badge bg-light text-dark border">
                            {highlights.length}
                          </span>
                        </div>

                        {highlights.length === 0 ? (
                          <div className="text-muted small mb-3">
                            No key highlights added yet.
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {highlights.map((highlight) => (
                              <span
                                key={highlight.id}
                                className="badge bg-light text-dark border d-flex align-items-center gap-2"
                                style={{ fontSize: "0.9rem" }}
                              >
                                {highlight.name}

                                {Number(highlight.maxPoints || 0) > 0 && (
                                  <span className="text-muted">
                                    {highlight.maxPoints} pts
                                  </span>
                                )}

                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
                                  onClick={() =>
                                    handleDeleteHighlight(
                                      factor.id,
                                      highlight.id
                                    )
                                  }
                                  disabled={saving}
                                  title="Delete highlight"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="border rounded p-3">
                        <div className="small fw-bold mb-2">Add Key Highlight</div>

                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-md-7">
                            <label className="form-label small">
                              Highlight Name
                            </label>

                            <input
                              type="text"
                              className="form-control"
                              value={newHighlightByFactor[factor.id] || ""}
                              onChange={(e) =>
                                setNewHighlightByFactor((current) => ({
                                  ...current,
                                  [factor.id]: e.target.value,
                                }))
                              }
                              placeholder="Example: Contour, Alignment, Lighting"
                            />
                          </div>

                          <div className="col-12 col-md-3">
                            <label className="form-label small">
                              Max Points
                            </label>

                            <input
                              type="number"
                              className="form-control"
                              value={
                                newHighlightMaxPointsByFactor[factor.id] || ""
                              }
                              onChange={(e) =>
                                setNewHighlightMaxPointsByFactor((current) => ({
                                  ...current,
                                  [factor.id]: e.target.value,
                                }))
                              }
                              placeholder="Optional"
                              min="0"
                            />
                          </div>

                          <div className="col-12 col-md-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary w-100"
                              onClick={() => handleAddHighlight(factor.id)}
                              disabled={saving}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );

};

export default SettingsScoring;